import { FixedColumn, StatusGridTable, StatusType } from "@/components/StatusGridTable";
import { StatusEnumStoreConciliation } from "@/enum/statusConciliation";
import { useGetConciliationStatusGridQuery } from "@/services/conciliation-status-grid";
import { getPreviousDay } from "@/utils/previousDay";
import { format } from "date-fns";
import { useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { IConciliationStatusGridStore } from "src/@types/conciliation-status-grid";
import { useFilterActions } from "../../../ducks/filter/hooks/actions";
import { useFilterState } from "../../../ducks/filter/hooks/filterState";
import { DateRangeType } from "../../../types";
import GridFilters from "./grid-filters";
import GridPagination from "./grid-pagination";

interface StatusGridViewRow extends IConciliationStatusGridStore {
  statusByDate: Record<string, StatusEnumStoreConciliation | null>;
  conciliationIdByDate: Record<string, number | null>;
  [key: string]: unknown;
}

interface StatusGridViewProps {
  dateRange: DateRangeType | undefined;
}

export default function StatusGridView({ dateRange }: StatusGridViewProps) {
  const navigate = useNavigate();
  const filterState = useFilterState();
  const { updateFilter } = useFilterActions();
  const { filters } = filterState || { filters: {} };

  const perPage = filters.meta?.perPage || 25;
  const currentPage = filters.meta?.page || 1;

  const startDate = dateRange?.from
    ? format(dateRange.from, "yyyy-MM-dd")
    : format(getPreviousDay(21), "yyyy-MM-dd");
  const endDate = dateRange?.to
    ? format(dateRange.to, "yyyy-MM-dd")
    : format(getPreviousDay(), "yyyy-MM-dd");

  const highlightStatuses = useMemo(() => {
    if (filters.status) {
      return filters.status.split(',').map((s: string) => s.trim());
    }
    return undefined;
  }, [filters.status]);

  const { data, isLoading, isFetching } = useGetConciliationStatusGridQuery({
    startDate,
    endDate,
    storeCode: filters.storeCode,
    regionalId: filters.regionalId,
    responsibleId: filters.responsibleId,
    page: currentPage,
    perPage,
  });

  const handlePageChange = (page: number) => {
    updateFilter({ key: "meta", value: { ...filters.meta, page } });
  };

  const handlePerPageChange = (newPerPage: number) => {
    updateFilter({ key: "meta", value: { ...filters.meta, perPage: newPerPage, page: 1 } });
  };

  const fixedColumns: FixedColumn<StatusGridViewRow>[] = [
    { key: "code", header: "Lojas", width: "80px" },
    { key: "city", header: "Cidade" },
    { key: "conferente", header: "Conferente" },
    { key: "regional", header: "Regional" },
  ];

  const gridData = useMemo<StatusGridViewRow[]>(() => {
    if (!data?.stores) return [];

    return data.stores.map((store) => ({
      ...store,
      statusByDate: data.statusMatrix[store.id] || {},
      conciliationIdByDate: data.conciliationIdMatrix?.[store.id] || {},
    }));
  }, [data]);

  const dateColumns = useMemo(() => {
    if (!data?.dates) return [];
    return data.dates.map((date) => {
      const d = new Date(date + "T00:00:00");
      return format(d, "dd-MMM");
    });
  }, [data?.dates]);

  const getStatus = (row: StatusGridViewRow, dateColumn: string): StatusType => {
    if (!data?.dates) return null;

    const dateIndex = dateColumns.indexOf(dateColumn);
    if (dateIndex === -1) return null;

    const originalDate = data.dates[dateIndex];
    const status = row.statusByDate[originalDate];

    return status as StatusType;
  };

  const handleCellClick = (row: StatusGridViewRow, dateColumn: string, status: StatusType) => {
    if (!data?.dates || status === "NOT_INTEGRATED") return;

    const dateIndex = dateColumns.indexOf(dateColumn);
    if (dateIndex === -1) return;

    const originalDate = data.dates[dateIndex];
    const conciliationId = row.conciliationIdByDate[originalDate];

    if (conciliationId) {
      navigate(`/conciliacao/lojas/conciliacao-de-caixa-de-loja/${conciliationId}`);
    }
  };

  return (
    <>
      <div className="mb-4">
        <GridFilters />
      </div>

      <div className='transition-all duration-300'>
        <StatusGridTable<StatusGridViewRow>
          data={gridData}
          fixedColumns={fixedColumns}
          dateColumns={dateColumns}
          getStatus={getStatus}
          onCellClick={handleCellClick}
          title="Grade de Status de Conciliações"
          isLoading={isLoading}
          isFetching={isFetching}
          emptyMessage="Nenhuma conciliação encontrada para o período selecionado"
          highlightStatuses={highlightStatuses}
        />
      </div>

      {data?.meta && (
        <GridPagination
          meta={data.meta}
          totalRows={data.meta.total}
          perPage={perPage}
          onPageChange={handlePageChange}
          onPerPageChange={handlePerPageChange}
        />
      )}
    </>
  );
}
